/* eslint-disable max-lines */
import More from '@diligentcorp/atlas-react-icons/dist/esm/lens/More.js';
import {
  LensTheme,
  OverflowBreadcrumbs,
  StatusIndicator,
} from '@diligentcorp/atlas-theme-mui-lens';
import { PageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DisclosuresApprovalDrawer } from 'src/components/atlas/organisms/DisclosuresApprovalDrawer/DisclosuresApprovalDrawer';
import { CoiStatusColorMap } from 'src/components/atlas/organisms/helpers/CoiStatusColorMap';
import { SpeakUpStatusColorMap } from 'src/components/atlas/organisms/helpers/SpeakUpColorMap';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { getGroupedTags } from 'src/components/molecules/CaseScreenTitle/helpers/getGroupedTags';
import { caseTagUpdated } from 'src/components/pages/Case/actions/caseTagUpdated';
import {
  casePageTitleBar,
  casePageVm,
  reportDetailsVm,
} from 'src/components/pages/Case/Case.slice';
import {
  CaseByIdQuery,
  TagType,
  useAddTagToCaseMutation,
  useGetCaseLinksByCaseIdQuery,
  useMitigationPlansForDisclosureQuery,
  useRemoveTagFromCaseMutation,
  useUpdateCaseDescriptionMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import { CaseTypeEnum, getReadableIdWithDash } from 'src/types';
import { capitaliseFirstLetter } from 'src/utils';
import { CaseSummary } from '../CaseSummary';
import { LinkCaseModal } from '../LinkCaseModal';
import { DownloadReportButton } from '../NewReportDetails/components';

type MenuItemsSharedProps = {
  sp: Record<number, { value: string }>;
  open: boolean;
  anchorEl: null | HTMLElement;
  handleClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  openMessages?: (reportId: string) => void;
  reportId?: string;
  canViewReportMessages: boolean;
  canSendReportMessages: boolean;
  unreadMessagesCount: number;
};

const CoiMenuItems = ({
  view,
  sp,
  open,
  anchorEl,
  handleClick,
  handleClose,
  openMessages,
  reportId,
  canViewReportMessages,
  canSendReportMessages,
  unreadMessagesCount,
  userHasEditWorkspaceAccess,
  caseStatus,
  isMitigationLoading,
  isMitigationError,
  isMitigationPlansAvailable,
  onMitigate,
  handleOnApprove,
}: MenuItemsSharedProps & {
  view: 'limited' | 'full';
  caseStatus?: string;
  userHasEditWorkspaceAccess: boolean;
  onMitigate?: () => void;
  isMitigationLoading?: boolean;
  isMitigationError?: boolean;
  isMitigationPlansAvailable?: boolean;
  handleOnApprove?: () => void;
}) => {
  const messagesButtonLabel = canSendReportMessages
    ? 'Chat with reporter'
    : 'View Messages';

  return view === 'full' ? (
    <Box sx={{ display: 'flex', gap: sp[2].value, flexDirection: 'row' }}>
      <Badge
        color="error"
        variant="dot"
        badgeContent={unreadMessagesCount}
        data-testid="unread-messages-menu-badge"
      >
        <Button
          variant="text"
          size="small"
          aria-label="More options"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <More />
        </Button>
      </Badge>
      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { 'aria-labelledby': 'more-button' } }}
      >
        <MenuItem
          onClick={() => {
            openMessages?.(String(reportId));
            handleClose();
          }}
          disabled={!openMessages || !reportId || !canViewReportMessages}
          divider
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
            }}
          >
            <ListItemText>{messagesButtonLabel}</ListItemText>
            {!!unreadMessagesCount && (
              <Badge
                color="error"
                badgeContent={unreadMessagesCount}
                data-testid="unread-messages-badge"
                sx={{
                  '& .MuiBadge-badge': {
                    position: 'static',
                    transform: 'none',
                  },
                }}
              />
            )}
          </Box>
        </MenuItem>
        {reportId && (
          <MenuItem divider>
            <DownloadReportButton reportId={reportId} isPartOfMenu={true} />
          </MenuItem>
        )}
      </Menu>
      <Button
        variant="outlined"
        size="small"
        disabled={!userHasEditWorkspaceAccess || caseStatus === 'CLOSED'}
        onClick={onMitigate}
      >
        Mitigate
      </Button>
      <Button
        variant="contained"
        disabled={
          !userHasEditWorkspaceAccess ||
          isMitigationLoading ||
          isMitigationError ||
          isMitigationPlansAvailable
        }
        size="small"
        onClick={handleOnApprove}
      >
        Approve
      </Button>
    </Box>
  ) : undefined;
};

const SpeakUpMenuItems = ({
  sp,
  caseType,
  open,
  anchorEl,
  handleClick,
  handleClose,
  openMessages,
  reportId,
  canViewReportMessages,
  canSendReportMessages,
  unreadMessagesCount,
  canEditCaseDetails,
  canSeeCaseSummary,
  handleSummaryClick,
  openLinkCaseModal,
  groupedOptions,
  selectedOptions,
  onTagToggle,
}: MenuItemsSharedProps & {
  caseType: CaseTypeEnum;
  canSeeCaseSummary?: boolean;
  handleSummaryClick?: () => void;
  canEditCaseDetails: boolean;
  openLinkCaseModal: (e: MouseEvent) => void;
  groupedOptions: ReturnType<typeof getGroupedTags>['groupedOptions'];
  selectedOptions: ReturnType<typeof getGroupedTags>['selectedOptions'];
  onTagToggle: (tagId: string) => void;
}) => {
  const messagesButtonLabel = canSendReportMessages
    ? 'Chat with reporter'
    : 'View Messages';

  const canLinkCases = caseType === CaseTypeEnum.SPEAK_UP && canEditCaseDetails;

  const tagBtnRef = useRef<HTMLButtonElement>(null);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', gap: sp[2].value, flexDirection: 'row' }}>
      <Button
        variant="text"
        size="small"
        aria-label="More options"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <More />
      </Button>
      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { 'aria-labelledby': 'more-button' } }}
      >
        <MenuItem
          onClick={handleSummaryClick}
          disabled={!canSeeCaseSummary}
          divider
        >
          Case summary
        </MenuItem>
        {reportId && (
          <MenuItem divider>
            <DownloadReportButton reportId={reportId} isPartOfMenu={true} />
          </MenuItem>
        )}
      </Menu>
      <Button
        ref={tagBtnRef}
        variant="outlined"
        size="small"
        disabled={!canEditCaseDetails}
        aria-controls={tagMenuOpen ? 'tag-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={tagMenuOpen ? 'true' : undefined}
        onClick={() => setTagMenuOpen(true)}
        data-testid="add-tag-dropdown-button"
      >
        Add tags
      </Button>
      <Menu
        id="tag-menu"
        anchorEl={tagBtnRef.current}
        open={tagMenuOpen}
        onClose={() => setTagMenuOpen(false)}
        slotProps={{ list: { 'aria-labelledby': 'add-tag-dropdown-button' } }}
      >
        {groupedOptions.flatMap((group) =>
          group.options.map((opt) => {
            const checked = selectedOptions.some((s) => s.value === opt.value);
            return (
              <MenuItem
                key={opt.value}
                onClick={() => onTagToggle(opt.value)}
                selected={checked}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    slotProps={{
                      input: { 'aria-labelledby': `tag-${opt.value}` },
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={`tag-${opt.value}`}>{opt.label}</ListItemText>
              </MenuItem>
            );
          }),
        )}
      </Menu>
      <Badge
        color="error"
        badgeContent={unreadMessagesCount}
        invisible={!unreadMessagesCount}
        data-testid="unread-messages-badge"
      >
        <Button
          variant="outlined"
          size="small"
          disabled={!openMessages || !reportId || !canViewReportMessages}
          onClick={() => {
            openMessages?.(String(reportId));
            handleClose();
          }}
        >
          <ListItemText>{messagesButtonLabel}</ListItemText>
        </Button>
      </Badge>
      <Button
        variant="contained"
        disabled={!canLinkCases}
        size="small"
        onClick={openLinkCaseModal}
      >
        Link case
      </Button>
    </Box>
  );
};

export const DisclosureCaseHeader = ({
  caseStatus,
  openMessages,
  reportId,
  caseId,
  onMitigate,
  view = 'full',
  refetch,
  caseType,
}: {
  caseStatus?: string;
  openMessages?: (reportId: string) => void;
  reportId?: string;
  caseId?: string;
  onMitigate?: () => void;
  view?: 'limited' | 'full';
  refetch?: () => void;
  caseType: CaseTypeEnum;
}) => {
  const reduxCase = useSelector(casePageVm);
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme<LensTheme>();
  const [caseSummaryIsOpen, setCaseSummaryIsOpen] = useState(false);
  const [linkCaseModalOpen, setLinkCaseModalOpen] = useState(false);

  const sp = theme.tokens.semantic.spacing.desktop.gap;
  const {
    permissions: { canViewReportMessages, canSendReportMessages },
    unreadMessagesCount,
  } = useSelector(reportDetailsVm({ reportId: reportId ?? '' }));
  const {
    permissions: { canEditCaseDetails, canViewCaseDetails } = {},
    caseTags = [] as TagType[],
    organisationTags = [],
    description,
    descriptionLastUpdated,
    importedCaseOriginalId,
    isImportedCase,
  } = useSelector(casePageTitleBar) || {};
  const [isApproveDrawerOpen, setIsApproveDrawerOpen] = useState(false);

  const history = useHistory();

  const addCaseTag = useAddTagToCaseMutation();
  const removeCaseTag = useRemoveTagFromCaseMutation();

  const { refetch: refetchCaseLinks } = useGetCaseLinksByCaseIdQuery(
    { caseId: caseId ?? '' },
    { enabled: false, retry: false },
  );
  const updateCaseSummary = useUpdateCaseDescriptionMutation({
    onMutate: async (variables) => {
      await queryClient.cancelQueries(['CaseById', { id: variables.caseId }]);
      const existingData =
        queryClient.getQueryData<CaseByIdQuery>([
          'CaseById',
          { id: variables.caseId },
        ]) ?? {};

      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: {
          ...existingData.caseById,
          description: variables.description,
          descriptionUpdatedAt: new Date().toISOString(),
        },
      });

      return {
        previousCaseData: existingData.caseById,
      };
    },
    onSuccess: () => {
      handleSuccessToast({
        toastMessage: 'Case summary updated',
      });
    },
    onError: (_, variables, context) => {
      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: context?.previousCaseData,
      });

      handleErrorToast({
        toastMessage: 'Failed to update case summary',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['CaseById', { id: caseId }]);
    },
  });

  const canSeeCaseSummary =
    (canViewCaseDetails && Boolean(description)) || Boolean(canEditCaseDetails);

  const handleSummaryClick = () => {
    canSeeCaseSummary && setCaseSummaryIsOpen(!caseSummaryIsOpen);
  };

  const handleUpdateCaseSummary = (description: string) => {
    if (!caseId) return;
    updateCaseSummary.mutate({
      description: description === '<p><br></p>' ? null : description,
      caseId,
    });
  };

  const openLinkCaseModal = (e: MouseEvent) => {
    e.stopPropagation();
    setLinkCaseModalOpen(true);
  };

  const { groupedOptions, selectedOptions } = getGroupedTags(
    organisationTags,
    caseTags,
  );

  const onTagToggle = (tagId: string) => {
    if (!caseId) return;
    const isSelected = caseTags.some((t) => t.id === tagId);
    const next = isSelected
      ? caseTags.filter((t) => t.id !== tagId)
      : [...caseTags, organisationTags.find((t) => t.id === tagId)!];
    dispatch(
      caseTagUpdated({
        caseId,
        selectedTagOptions: next.map((t) => ({ value: t.id, label: t.name })),
        mutationAddCaseTag: addCaseTag.mutateAsync,
        mutationRemoveCaseTag: removeCaseTag.mutateAsync,
        queryClient,
      }),
    );
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(caseType === CaseTypeEnum.COI ? '/disclosures' : '/cases');
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const userHasEditWorkspaceAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  const {
    data: mitigationData,
    isLoading: isMitigationLoading,
    isError: isMitigationError,
  } = useMitigationPlansForDisclosureQuery(
    { caseId: caseId ?? '' },
    {
      enabled:
        userHasEditWorkspaceAccess &&
        Boolean(caseId) &&
        caseType === CaseTypeEnum.COI,
    },
  );

  const isMitigationPlansAvailable =
    caseType === CaseTypeEnum.COI &&
    !isMitigationLoading &&
    !isMitigationError &&
    (mitigationData?.mitigationPlansForDisclosure?.length ?? 0) > 0;

  useEffect(() => {
    if (isMitigationError) {
      console.error('DisclosureCaseHeader: failed to load mitigation plans', {
        caseId,
      });
    }
  }, [isMitigationError, caseId]);

  const handleOnApprove = () => {
    setIsApproveDrawerOpen(true);
  };

  const pageType = caseType === CaseTypeEnum.COI ? 'Disclosure' : 'Case';
  const caseReadableId = caseId ? getReadableIdWithDash(caseId) : undefined;
  const importedSubTitle = importedCaseOriginalId
    ? `Original Case ID: ${importedCaseOriginalId}`
    : '';

  return (
    <>
      <PageHeader
        pageTitle={
          caseReadableId
            ? `${pageType} ${caseReadableId?.toUpperCase()}`
            : pageType
        }
        pageSubtitle={isImportedCase ? importedSubTitle : null}
        data-testid="case-detail-header"
        statusIndicator={
          <StatusIndicator
            customColor={
              caseType === CaseTypeEnum.COI
                ? CoiStatusColorMap(caseStatus)
                : SpeakUpStatusColorMap(caseStatus)
            }
            label={capitaliseFirstLetter(caseStatus)}
            aria-label={`Case status - ${caseStatus}`}
          />
        }
        breadcrumbs={
          <OverflowBreadcrumbs
            items={[
              {
                id: '1',
                label: `Manage ${pageType}s`,
                url: `/${pageType.toLowerCase()}s`,
              },
              { id: '2', label: `${pageType} details`, url: '' },
            ]}
            hideLastItem={false}
            aria-label="Breadcrumbs"
          >
            {({ label, url }) => <Link href={url}>{label}</Link>}
          </OverflowBreadcrumbs>
        }
        slotProps={{
          backButton: {
            'aria-label': 'Back to users list',
            onClick: handleBack,
          },
        }}
        moreButton={
          caseType === CaseTypeEnum.COI ? (
            <CoiMenuItems
              view={view}
              sp={sp}
              open={open}
              anchorEl={anchorEl}
              handleClick={handleClick}
              handleClose={handleClose}
              openMessages={openMessages}
              reportId={reportId}
              canViewReportMessages={canViewReportMessages ?? false}
              canSendReportMessages={canSendReportMessages ?? false}
              unreadMessagesCount={unreadMessagesCount ?? 0}
              userHasEditWorkspaceAccess={userHasEditWorkspaceAccess}
              caseStatus={caseStatus}
              isMitigationLoading={isMitigationLoading}
              isMitigationError={isMitigationError}
              isMitigationPlansAvailable={isMitigationPlansAvailable}
              onMitigate={onMitigate}
              handleOnApprove={handleOnApprove}
            />
          ) : (
            <SpeakUpMenuItems
              sp={sp}
              open={open}
              anchorEl={anchorEl}
              handleClick={handleClick}
              handleClose={handleClose}
              openMessages={openMessages}
              reportId={reportId}
              canViewReportMessages={canViewReportMessages ?? false}
              canSendReportMessages={canSendReportMessages ?? false}
              canEditCaseDetails={canEditCaseDetails ?? false}
              unreadMessagesCount={unreadMessagesCount ?? 0}
              caseType={caseType}
              canSeeCaseSummary={canSeeCaseSummary}
              handleSummaryClick={handleSummaryClick}
              openLinkCaseModal={openLinkCaseModal}
              groupedOptions={groupedOptions}
              selectedOptions={selectedOptions}
              onTagToggle={onTagToggle}
            />
          )
        }
      />
      {caseId && caseType === CaseTypeEnum.COI && (
        <DisclosuresApprovalDrawer
          isOpen={isApproveDrawerOpen}
          onClose={() => setIsApproveDrawerOpen(false)}
          caseId={caseId}
          refetch={refetch ?? (() => {})}
        />
      )}
      {caseType === CaseTypeEnum.SPEAK_UP && (
        <CaseSummary
          isOpen={caseSummaryIsOpen}
          closeSummary={() => {
            setCaseSummaryIsOpen(false);
          }}
          saveSummary={(description: string) =>
            handleUpdateCaseSummary(description)
          }
          descriptionLastUpdated={descriptionLastUpdated}
          description={description}
          hasCanEditPermissions={Boolean(canEditCaseDetails)}
        />
      )}
      {caseType === CaseTypeEnum.SPEAK_UP && caseId && (
        <LinkCaseModal
          caseId={caseId}
          readableId={caseReadableId}
          open={linkCaseModalOpen}
          setOpen={setLinkCaseModalOpen}
          refetchCaseLinks={refetchCaseLinks}
        />
      )}
    </>
  );
};
